import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, errText } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Loader2, Inbox, AlertTriangle } from 'lucide-react';

/**
 * `/claim/:token` — where the invite email sends an office that is not on
 * DoctorPaperwork yet (ADR 0008 decision 5). It names no patient: the patient
 * is shown only inside a practice's Patient requests list, after sign-in.
 * Signing up (or a signed-in staff member) puts the request in that list;
 * accepting it is still a separate, explicit step there.
 */
export default function ClaimRequest() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useApp();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/patient-requests/claim/${token}`)
      .then((r) => setInfo(r.data))
      .catch((err) => setError(errText(err, 'This link is not valid.')));
  }, [token]);

  const staff = user && ['nurse', 'admin'].includes(user.role);

  const claim = async () => {
    setBusy(true);
    try {
      await api.post(`/practice/patient-requests/claim/${token}`);
      navigate('/patient-requests');
    } catch (err) {
      toast.error(errText(err));
      setBusy(false);
    }
  };

  const signupUrl = info ? `/signup?claim=${encodeURIComponent(token)}&email=${encodeURIComponent(info.office_email)}` : '/signup';

  return (
    <div className="min-h-screen hero-wash grid place-items-center p-4 sm:p-6">
      <Card className="w-full max-w-md" data-testid="pd-claim-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2"><Inbox className="h-5 w-5 text-primary" /> A patient asked for your help</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          {error ? (
            <p className="flex items-start gap-2 text-[hsl(0_72%_40%)]"><AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {error}</p>
          ) : !info ? (
            <p className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>
          ) : !info.open ? (
            <p className="text-muted-foreground" data-testid="pd-claim-closed">This request is closed. Nothing more is needed from your office.</p>
          ) : (
            <>
              <p className="text-muted-foreground">
                A patient has asked your office to complete their medical-leave paperwork through DoctorPaperwork.
                They have already paid for it, so it costs your practice nothing.
              </p>
              <p className="text-muted-foreground">
                {info.days_left != null && <>The request closes in {info.days_left} day(s) if nobody answers. </>}
                You’ll see who the patient is once you’re signed in, and nothing happens until you accept.
              </p>
              {staff ? (
                <Button className="w-full" onClick={claim} disabled={busy} data-testid="pd-claim-add">
                  {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Add to my practice’s Patient requests
                </Button>
              ) : (
                <div className="space-y-2">
                  <Button asChild className="w-full" data-testid="pd-claim-signup"><Link to={signupUrl}>Set up your practice account</Link></Button>
                  <Button asChild variant="outline" className="w-full">
                    <Link to="/login" state={{ from: `/claim/${token}` }}>I already have an account</Link>
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
